import * as React from 'react'
import { Checkbox, Select } from '@mobiscroll/react-latest'
import {
  IServiceObject,
  ITeamMemberPermissionState,
  ICreateTeamMemberPermissionDTO,
  IUpdateTeamMemberPermissionDTO
} from '../../../types'
import PreloaderCircle from '../../ui/PreloaderCircle'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import UIMessages from 'src/utils/ui-messages'
import Divider from 'src/components/ui/Divider'
import TeamMemberBottomPanel from './TeamMemberBottomPanel'

export interface TeamMemberPermissionEditFormProps {
  id: string
  orgId: string
  permission: ITeamMemberPermissionState
  services: IServiceObject[]
  createTeamMemberPermission: (tmp: ICreateTeamMemberPermissionDTO) => void
  fetchServices: (orgId: string) => void
  fetchTeamMemberPermission: (id: string) => void
  handleRedirect: () => void
  updateTeamMemberPermission: (tmp: IUpdateTeamMemberPermissionDTO) => void
}

export interface TeamMemberPermissionEditFormState {
  selectedServices: any
}

class TeamMemberPermissionEditForm extends React.Component<
  TeamMemberPermissionEditFormProps,
  TeamMemberPermissionEditFormState
> {
  constructor(props: TeamMemberPermissionEditFormProps) {
    super(props)
    this.state = {
      selectedServices: {}
    }
  }

  public componentDidMount() {
    this.props.fetchServices(this.props.orgId)
    this.props.fetchTeamMemberPermission(this.props.id)
  }

  public componentDidUpdate(prevProps: TeamMemberPermissionEditFormProps) {
    const { servicePermission } = this.props.permission.permission || { servicePermission: {} }
    const { servicePermission: prevServicePermission } = prevProps.permission.permission || { servicePermission: {} }
    if (Object.keys(prevServicePermission).length !== Object.keys(servicePermission).length) {
      if (Object.keys(servicePermission).length > 0) {
        this.setState({
          selectedServices: servicePermission
        })
      }
    }
    const { services } = this.props
    if (services.length > 0 && Object.keys(this.state.selectedServices).length === 0) {
      const allServices = {}
      this.props.services.forEach(svc => {
        allServices[svc.id] = true
      })
      this.setState({
        selectedServices: allServices
      })
    }
  }

  public render() {
    const { permission, services, handleRedirect } = this.props
    const { isFetching } = permission
    const { resourcePermissions } = permission.permission || {
      resourcePermissions: {
        organization_info: true,
        team_members: true,
        station_user: true,
        services: true,
        riders: true,
        products: true,
        drivers: true,
        vehicles: true,
        trips_management: true,
        billing_management: true,
        global_settings: true,
        promotions: true,
        rider_driver_feedbacks: true
      }
    }
    const { selectedServices: servicePermission } = this.state
    const {
      billing_management: billingManagementPermission,
      drivers: driversPermission,
      global_settings: globalSettingsPermission,
      organization_info: organizationInfoPermission,
      products: productsPermmission,
      promotions: promotionsPermission,
      rider_driver_feedbacks: riderDriverFeedbacksPermission,
      riders: ridersPermission,
      services: servicesPermission,
      station_user: stationUserPermission,
      team_members: teamMembersPermission,
      trips_management: tripsManagementPermission,
      vehicles: vehiclesPermission
    } = resourcePermissions || false

    const {
      billingManagement,
      drivers,
      globalSettings,
      organizationInfo,
      products,
      promotions,
      resources,
      riderDriverFeedbacks,
      riders,
      service,
      services: servicesLabel,
      servicesPlaceholder,
      stationUser,
      teamMembers,
      tripsManagement,
      vehicles
    } = UIMessages.teamMember.form.permission
    const inputProps = {
      inputStyle: 'box' as const,
      labelStyle: 'stacked' as const,
      touchUi: false,
      theme: 'ios'
    }

    const serviceOptions = services.map(p => ({ text: p.name, value: p.id }))
    const selectedService = services.filter(svc => servicePermission[svc.id]).map(svc => svc.id)

    return (
      <section className='tm-permission-form-container'>
        <FloatingForm>
          <div className='mbsc-grid mb3' style={{ padding: '0' }}>
            {isFetching && (
              <div className='flex justify-center items-center'>
                <PreloaderCircle loading={isFetching} />
              </div>
            )}
            {!isFetching && (
              <div className='mbsc-row'>
                {/* RESOURCES */}
                <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                  <p className='bold font-grey3'>{resources}</p>
                  <Divider />
                  <form id='resources-form'>
                    <div className='mbsc-row'>
                      <div className='mbsc-col-12 mbsc-col-lg-6 pt2'>
                        <label className='flex'>
                          <Checkbox defaultChecked={organizationInfoPermission} name='organization_info' />
                          <span className='font-grey3 ml1'>{organizationInfo}</span>
                        </label>
                      </div>
                      <div className='mbsc-col-12 mbsc-col-lg-6 pt2'>
                        <label className='flex'>
                          <Checkbox defaultChecked={teamMembersPermission} name='team_members' />
                          <span className='font-grey3 ml1'>{teamMembers}</span>
                        </label>
                      </div>
                      <div className='mbsc-col-12 mbsc-col-lg-6 pt2'>
                        <label className='flex'>
                          <Checkbox defaultChecked={stationUserPermission} name='station_user' />
                          <span className='font-grey3 ml1'>{stationUser}</span>
                        </label>
                      </div>
                      <div className='mbsc-col-12 mbsc-col-lg-6 pt2'>
                        <label className='flex'>
                          <Checkbox defaultChecked={servicesPermission} name='services' />
                          <span className='font-grey3 ml1'>{servicesLabel}</span>
                        </label>
                      </div>
                      <div className='mbsc-col-12 mbsc-col-lg-6 pt2'>
                        <label className='flex'>
                          <Checkbox defaultChecked={ridersPermission} name='riders' />
                          <span className='font-grey3 ml1'>{riders}</span>
                        </label>
                      </div>
                      <div className='mbsc-col-12 mbsc-col-lg-6 pt2'>
                        <label className='flex'>
                          <Checkbox defaultChecked={productsPermmission} name='products' />
                          <span className='font-grey3 ml1'>{products}</span>
                        </label>
                      </div>
                      <div className='mbsc-col-12 mbsc-col-lg-6 pt2'>
                        <label className='flex'>
                          <Checkbox defaultChecked={driversPermission} name='drivers' />
                          <span className='font-grey3 ml1'>{drivers}</span>
                        </label>
                      </div>
                      <div className='mbsc-col-12 mbsc-col-lg-6 pt2'>
                        <label className='flex'>
                          <Checkbox defaultChecked={vehiclesPermission} name='vehicles' />
                          <span className='font-grey3 ml1'>{vehicles}</span>
                        </label>
                      </div>
                      <div className='mbsc-col-12 mbsc-col-lg-6 pt2'>
                        <label className='flex'>
                          <Checkbox defaultChecked={tripsManagementPermission} name='trips_management' />
                          <span className='font-grey3 ml1'>{tripsManagement}</span>
                        </label>
                      </div>
                      <div className='mbsc-col-12 mbsc-col-lg-6 pt2'>
                        <label className='flex'>
                          <Checkbox defaultChecked={billingManagementPermission} name='billing_management' />
                          <span className='font-grey3 ml1'>{billingManagement}</span>
                        </label>
                      </div>
                      <div className='mbsc-col-12 mbsc-col-lg-6 pt2'>
                        <label className='flex'>
                          <Checkbox defaultChecked={globalSettingsPermission} name='global_settings' />
                          <span className='font-grey3 ml1'>{globalSettings}</span>
                        </label>
                      </div>
                      <div className='mbsc-col-12 mbsc-col-lg-6 pt2'>
                        <label className='flex'>
                          <Checkbox defaultChecked={promotionsPermission} name='promotions' />
                          <span className='font-grey3 ml1'>{promotions}</span>
                        </label>
                      </div>
                      <div className='mbsc-col-12 mbsc-col-lg-6 pt2'>
                        <label className='flex'>
                          <Checkbox defaultChecked={riderDriverFeedbacksPermission} name='rider_driver_feedbacks' />
                          <span className='font-grey3 ml1'>{riderDriverFeedbacks}</span>
                        </label>
                      </div>
                    </div>
                  </form>
                </div>
                {/* SERVICE */}
                {resourcePermissions.services && (
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <p className='bold font-grey3'>{service}</p>
                    <Divider />
                    <div className='mbsc-row'>
                      <div className='mbsc-col-12 pt2'>
                        <Select
                          {...inputProps}
                          data={serviceOptions}
                          label={servicesLabel}
                          placeholder={servicesPlaceholder}
                          selectMultiple={true}
                          value={selectedService}
                          onChange={this.changeServiceSelection}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </FloatingForm>
        <TeamMemberBottomPanel
          fetching={isFetching}
          processing={isFetching}
          onCancelClick={handleRedirect}
          onSubmitClick={this.saveTeamMemberPermission}
        />
      </section>
    )
  }

  private changeServiceSelection = (ev: any) => {
    const value: { [key: string]: boolean } = {}
    const unselectedService = Object.keys(this.state.selectedServices).filter(selected => !ev.value.includes(selected))

    ev.value.forEach((serviceId: string) => {
      value[serviceId] = true
    })
    unselectedService.forEach((serviceId: string) => {
      value[serviceId] = false
    })

    this.setState({
      selectedServices: {
        ...value
      }
    })
  }

  private saveTeamMemberPermission = async () => {
    const resourcesData: any = {}
    document.querySelectorAll('#resources-form input').forEach((elem: any) => {
      const attrName = `${elem.getAttribute('name')}`
      resourcesData[attrName] = !!elem.checked
    })
    const formData = {
      organizationTeamMemberId: this.props.id,
      resourcePermissions: resourcesData,
      servicePermission: this.state.selectedServices
    }
    const permission = this.props.permission.permission
    if (permission) {
      await this.props.updateTeamMemberPermission({
        id: permission.id,
        ...formData
      })
      this.props.handleRedirect()
    } else {
      await this.props.createTeamMemberPermission(formData)
    }
    this.props.fetchTeamMemberPermission(this.props.id)
  }
}

export default TeamMemberPermissionEditForm



// WEBPACK FOOTER //
// ./src/components/team-members/form/TeamMemberPermissionEditForm.tsx